import { useState } from "react";

/**
 * Reusable share bar for Facebook and X (Twitter).
 * - `variant`: "light" (default, for pale backgrounds) or "dark" (for the black sections).
 * - `align`: "start" (default) or "center".
 * - `label`: kicker text above the buttons.
 * - `text`: default share caption (X uses this as tweet body; FB uses as quote).
 */
export default function ShareBar({
    variant = "light",
    align = "start",
    label = "Share Danielle's story",
    text = "Danielle walked into a hospital with a cough. Forty-one days later she was gone. Read her story. Stand with her family. #JusticeForDanielle #DanielleAlvarez",
}) {
    const [copied, setCopied] = useState(false);

    const shareUrl =
        typeof window !== "undefined"
            ? window.location.origin || window.location.href
            : "https://danielle-41days.emergent.host";

    const encoded = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(text);

    const xHref = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encoded}`;
    const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encoded}&quote=${encodedText}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            /* ignore */
        }
    };

    const dark = variant === "dark";
    const labelClass = dark ? "text-neutral-400" : "text-neutral-500";
    const btnClass = dark
        ? "border border-neutral-700 text-neutral-100 hover:bg-neutral-50 hover:text-neutral-950 hover:border-neutral-50"
        : "border border-neutral-400 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-50 hover:border-neutral-900";

    return (
        <div
            data-testid="share-bar"
            className={`flex flex-col ${align === "center" ? "items-center text-center" : "items-start"} gap-4`}
        >
            <div
                className={`font-sans text-[10px] uppercase tracking-[0.35em] ${labelClass}`}
            >
                {label}
            </div>
            <div
                className={`flex flex-wrap gap-3 ${align === "center" ? "justify-center" : ""}`}
            >
                <a
                    href={xHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-x"
                    aria-label="Share on X (Twitter)"
                    className={`group inline-flex items-center gap-3 px-5 py-3 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors duration-500 ${btnClass}`}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path d="M18.244 2H21.5l-7.44 8.51L22.75 22h-6.883l-5.394-6.966L4.31 22H1.05l7.96-9.1L1.25 2h7.055l4.876 6.417L18.244 2Zm-1.207 18h1.84L7.083 3.86H5.11l11.926 16.14Z" />
                    </svg>
                    <span>Share on X</span>
                </a>
                <a
                    href={fbHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-facebook"
                    aria-label="Share on Facebook"
                    className={`group inline-flex items-center gap-3 px-5 py-3 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors duration-500 ${btnClass}`}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.5-3.9 3.79-3.9 1.1 0 2.24.2 2.24.2v2.47h-1.27c-1.25 0-1.64.78-1.64 1.58V12h2.79l-.45 2.89h-2.34v6.99A10 10 0 0 0 22 12Z" />
                    </svg>
                    <span>Share on Facebook</span>
                </a>
                <button
                    type="button"
                    onClick={copy}
                    data-testid="share-copy"
                    aria-label="Copy link"
                    className={`group inline-flex items-center gap-3 px-5 py-3 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors duration-500 ${btnClass}`}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        aria-hidden="true"
                    >
                        <path d="M10 14a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" />
                        <path d="M14 10a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" />
                    </svg>
                    <span>{copied ? "Link copied" : "Copy link"}</span>
                </button>
            </div>
        </div>
    );
}
